import { useEffect, useState } from "react";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import { ApolloError } from "@apollo/client";
import { NotePanel } from "@src/common/components/NotePanel";
import { useUpdateEndCustomerNoteMutation } from "@api/graphql/generated/generated-types";
import { useCreateOrderContext } from "../utils/CreateOrderContextProvider";
import { useAuth } from "@src/common/hooks/useAuth";
import { useCourieStore } from "@src/common/lib/store";
import { showErrorToast } from "@src/common/lib/NetworkErrorHandling";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";

export default function CustomerNotePanel() {
  const { selectedCustomer, setSelectedCustomer } = useCreateOrderContext();
  const { courierId } = useAuth();
  const { showToast } = useCourieStore();

  const [isEditing, setIsEditing] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [updateNote, { loading }] = useUpdateEndCustomerNoteMutation();

  useEffect(() => {
    setIsEditing(false);
    setNoteText("");
  }, [selectedCustomer?.id]);

  if (!selectedCustomer?.note) return null;

  function handleEdit() {
    setNoteText(selectedCustomer?.note || "");
    setIsEditing(true);
  }

  function handleCancel() {
    setNoteText(selectedCustomer?.note || "");
    setIsEditing(false);
  }

  function handleSave() {
    const currentCustomer = selectedCustomer!;
    // Send empty string (not null) to clear — backend does not accept null
    const noteToSave = noteText.trim();

    updateNote({
      variables: {
        courierId: courierId || "",
        endCustomerId: currentCustomer.id,
        note: noteToSave,
      },
    })
      .then(() => {
        setSelectedCustomer({ ...currentCustomer, note: noteToSave || null });
        setIsEditing(false);
        showToast({
          message: "Customer note updated",
          type: "success",
        });
      })
      .catch((err) => {
        showErrorToast(err as ApolloError, showToast);
      });
  }

  if (isEditing) {
    return (
      <div className="flex items-start gap-2.5 rounded-r-md border-l-[3px] border-amber-400 bg-amber-400/10 p-2.5 dark:bg-amber-400/[0.08]">
        <ExclamationTriangleIcon className="h-[18px] w-[18px] flex-shrink-0 text-amber-400 mt-0.5" />
        <div className="flex-1">
          <div className="text-xs font-semibold text-amber-400">Customer Note</div>
          <textarea
            className="mt-1 w-full resize-vertical rounded border border-gray-300 bg-white p-2 text-xs leading-relaxed text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
            rows={3}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            disabled={loading}
            autoFocus
          />
          <div className="mt-1.5 flex justify-end gap-2">
            <button
              type="button"
              className="rounded border border-gray-300 px-3 py-1 text-xs text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700 disabled:opacity-50"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rounded bg-blue-500 px-3 py-1 text-xs text-white hover:bg-blue-600 disabled:opacity-50"
              onClick={handleSave}
              disabled={loading}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <NotePanel
      note={selectedCustomer.note}
      actions={
        <button
          type="button"
          className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
          onClick={handleEdit}
        >
          <PencilSquareIcon className="h-3 w-3" />
          Edit
        </button>
      }
    />
  );
}
